import { formatGr, type Id } from '@odhaczka/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/db';
import type { ListItemRow, PromotionRow } from '../db/types';
import type { PriceIndex } from './prices';

// Promocje na artykuły domu (zapisuje je serwer): plakietki przy pozycjach list, w historii ceny i w zakładce Ceny.

export interface PromoIndex {
  /** Artykuł → promocje trwające teraz (najwcześniej kończąca się pierwsza). */
  byArticle: Map<Id, PromotionRow[]>;
  active: PromotionRow[];
}

export const isActivePromo = (p: PromotionRow, now = Date.now()) => !p.deleted && (p.startMs ?? 0) <= now && (p.endMs ?? 0) >= now;

export async function loadPromoIndex(now = Date.now()): Promise<PromoIndex> {
  const active = (await db.promotions.where('endMs').aboveOrEqual(now).toArray()).filter((p) => isActivePromo(p, now)).sort((a, b) => (a.endMs ?? 0) - (b.endMs ?? 0));
  const byArticle = new Map<Id, PromotionRow[]>();
  for (const p of active) if (p.articleId) byArticle.set(p.articleId, [...(byArticle.get(p.articleId) ?? []), p]);
  return { byArticle, active };
}

export function usePromoIndex(): PromoIndex | undefined {
  return useLiveQuery(() => loadPromoIndex());
}

/** Promocje dla pozycji listy: na przypięty artykuł albo na którykolwiek artykuł jej produktu. */
export function promosForItem(item: Pick<ListItemRow, 'articleId' | 'productId'>, prices: PriceIndex | undefined, promos: PromoIndex | undefined): PromotionRow[] {
  if (!promos || promos.active.length === 0) return [];
  const ids = new Set<Id>();
  if (item.articleId) ids.add(item.articleId);
  else if (item.productId && prices) for (const a of prices.byProduct.get(prices.resolveProduct(item.productId)) ?? []) ids.add(a.id);
  return [...ids].flatMap((id) => promos.byArticle.get(id) ?? []);
}

const untilLabel = (ms: number | null) =>
  ms ? new Date(ms).toLocaleDateString('pl-PL', { weekday: 'short', day: 'numeric', month: 'numeric' }) : '';

/** Krótko do plakietki: „−30% do pt. 16.10”, „2,49 zł z Lidl Plus”. */
export function promoShort(p: PromotionRow): string {
  const what = p.discount?.trim() || (p.priceGr ? formatGr(p.priceGr) : 'promocja');
  return `${what}${p.lidlPlus ? ' · Lidl Plus' : ''}${p.endMs ? ` · do ${untilLabel(p.endMs)}` : ''}`;
}

export const PROMO_SOURCE: Record<string, string> = {
  leaflet: 'gazetka',
  web: 'lidl.pl',
  offers: 'oferta sklepu',
  coupon: 'kupon Lidl Plus',
};
